import { createFileRoute, Link } from "@tanstack/react-router";
import BlogPostLayout, { blogPostHead } from "@/components/BlogPostLayout";
import { getPost } from "@/lib/blog-posts";

const post = getPost("questions-to-ask-before-paying-a-limo-deposit");

const faqs = [
  {
    q: "Is it normal to pay a deposit for a limo in Atlanta?",
    a: "Yes. Most reputable companies take a deposit to hold your date. Before you pay it, ask the questions in this guide and get the answers, plus the cancellation terms, in writing.",
  },
  {
    q: "How can I tell if a limo company subcontracts?",
    a: "Ask directly: who owns the vehicle, and will your own chauffeur drive it? A company that owns its fleet will answer without hesitation. Vague answers like 'one of our partners' usually mean the job may be handed off.",
  },
  {
    q: "What should be in writing before I pay?",
    a: "The vehicle you are booking, what is included and what costs extra, the cancellation policy, and the total quote. If it isn't written down, assume it isn't guaranteed.",
  },
];

export const Route = createFileRoute("/blog/questions-to-ask-before-paying-a-limo-deposit")({
  head: () => blogPostHead(post, faqs),
  component: Post,
});

function Q({ n, q, why, good }: { n: number; q: string; why: string[]; good: string[] }) {
  return (
    <>
      <h2>
        {n}. {q}
      </h2>
      <h3>Why it matters</h3>
      {why.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
      <h3>What a good answer sounds like</h3>
      {good.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

function Post() {
  return (
    <BlogPostLayout post={post} faqs={faqs}>
      <p>
        A limo deposit feels like a small step. You pick a date, see a few photos, pay to hold it and move on to
        the next item on your list. The trouble shows up later, on the night itself, when a different vehicle
        pulls up with a driver nobody at the company seems to know.
      </p>
      <p>
        I run a small, family-owned limousine company in Atlanta, and I hear these stories from new clients all
        the time. Almost every one could have been avoided with a few questions before the deposit was paid. Here
        are the nine I would ask any company, including mine.
      </p>

      <Q
        n={1}
        q="Who owns the vehicle?"
        why={[
          "Many companies don't own the vehicles they advertise. They take your booking, then look for someone with a free car. The company you trusted may never touch your ride.",
        ]}
        good={[
          "\"We do. Every vehicle we send is ours.\" A clear, immediate answer. If you hear \"we work with a network\" or \"it depends on availability,\" keep asking.",
        ]}
      />
      <Q
        n={2}
        q="Will a subcontractor show up?"
        why={[
          "Subcontracting is the most common reason the night doesn't match the booking. A subcontractor doesn't know your plans, doesn't answer to the company you paid, and may not meet the same standards.",
        ]}
        good={[
          "A good company tells you plainly whether your ride is one of their own vehicles, and names your vehicle and chauffeur in writing before pickup.",
        ]}
      />
      <Q
        n={3}
        q="Can I see the exact vehicle?"
        why={[
          "Stock photos and borrowed pictures are everywhere in this industry. The cabin in the ad may not exist in the company's fleet at all.",
        ]}
        good={[
          "\"Yes, the vehicles in our photos are ours, and the one you book is the one that arrives.\" A company proud of its fleet will gladly show you more photos or let you see it in person.",
        ]}
      />
      <Q
        n={4}
        q="Is the chauffeur background-checked and fingerprinted?"
        why={[
          "You are handing your family, your guests or your executives to a stranger for the evening. A background check alone is good; fingerprinting adds another layer of verification.",
        ]}
        good={[
          "\"Yes, every chauffeur passes background checks and fingerprinting.\" Bonus points if they tell you how long their chauffeurs have been with them. Long tenure usually means a company treats its people well.",
        ]}
      />
      <Q
        n={5}
        q="How old is the vehicle?"
        why={[
          "Older vehicles are more likely to have worn interiors, tired air conditioning and mechanical problems. \"Late model\" can mean almost anything.",
        ]}
        good={[
          "A specific model year, not a vague description. Our own fleet is 2025 or newer and custom-built, and a good company should be just as specific about theirs.",
        ]}
      />
      <Q
        n={6}
        q="What is included, and what is extra?"
        why={[
          "Two quotes can look similar until the final bill arrives. Gratuity, fuel, cleaning, wait time and extra hours are often handled differently from company to company.",
        ]}
        good={[
          "A written quote that lists what is included and what could cost extra, before you pay anything. If a company won't put it in writing, that is your answer.",
        ]}
      />
      <Q
        n={7}
        q="What happens if the vehicle breaks down?"
        why={[
          "Even well-kept vehicles can have a problem. What matters is whether the company has a real plan, or whether you will be standing on a curb making phone calls.",
        ]}
        good={[
          "A specific plan: who you call, how a replacement reaches you, and roughly how long it takes. Be wary of \"that never happens.\"",
        ]}
      />
      <Q
        n={8}
        q="What is the cancellation policy?"
        why={[
          "Plans change. Weather, illness and schedule changes happen, and cancellation terms vary widely between companies. Many disputes come from terms that were only ever spoken, never written.",
        ]}
        good={[
          "A clear policy you can read before paying. Ask for it in writing and keep a copy. Whatever the terms are, you should know them before your deposit leaves your account.",
        ]}
      />
      <Q
        n={9}
        q="Is the company licensed and insured?"
        why={[
          "Commercial passenger transportation requires proper operating authority and commercial insurance. If something goes wrong, this is what protects you.",
        ]}
        good={[
          "The company should share its operating authority number and proof of insurance when asked. You can look up interstate carriers in the FMCSA database, and in Georgia, check with the Department of Public Safety, which regulates limousine carriers. A company that hesitates to share these details is telling you something.",
        ]}
      />

      <h2>The One Belief Behind All Nine Questions</h2>
      <p>
        Every question on this list comes back to one idea: <strong>the vehicle and chauffeur you book should be
        the ones that show up.</strong> That is the promise we built our company around. We own every vehicle in
        our fleet, our chauffeurs are background-checked, fingerprinted and long-tenured, and every
        vehicle is detailed before and after every job.
      </p>
      <p>
        If you want to see how we answer each of these questions ourselves, read{" "}
        <Link to="/our-standard">the Empress Elite Standard</Link>. You can also browse our{" "}
        <Link to="/sprinter-limo-atlanta">Sprinter Limo fleet</Link>, learn about our{" "}
        <Link to="/wedding-transportation-atlanta">wedding transportation</Link>, or read{" "}
        <Link to="/our-story">our story</Link>.
      </p>
      <p>
        Ask the questions. Get the answers in writing. Then book with confidence, whoever you choose.
      </p>
    </BlogPostLayout>
  );
}
